import type {
  WebSocketConnectionStatus,
  SubscriptionTarget,
  WSServerMessage,
  WSCallbacks,
} from './types';

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
const PING_INTERVAL = 30000;

// Ticket #91 §4.4: middleware 0.7.18 DROPS a user's open connections when their
// rights shrink — an admin role or privilege taken away, a limits.json save that
// puts them on a project's MW light list or takes them off its Partner / Chain
// manager / Manager list, a group's chain managers changed, a project moved out
// of a group — and when their sessions end. It sends no close frame; the socket
// simply goes. The console MUST come back and subscribe again, so that the new
// subscriptions follow the rights the user holds NOW.
//
// That is what the onclose → scheduleReconnect → (onopen, 'connected') →
// StoreImpl.resubscribeAll() path below already does, with the exponential
// backoff that keeps a fleet-wide disconnect from becoming a thundering herd.
// Nothing here needs to know WHY the connection went.

export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private callbacks: WSCallbacks;
  private manualClose = false;
  private reconnectDelay = RECONNECT_BASE_DELAY;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(callbacks: WSCallbacks) {
    this.callbacks = callbacks;
    this.url = this.buildUrl();
  }

  private buildUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/message`;
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.manualClose = false;
    this.callbacks.onStatusChange('connecting');

    try {
      this.ws = new WebSocket(this.url);
    } catch (e) {
      console.error('WebSocketService: Failed to create WebSocket:', e);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log('WebSocketService: Connected');
      this.reconnectDelay = RECONNECT_BASE_DELAY;
      this.callbacks.onStatusChange('connected');
      this.startPing();
    };

    this.ws.onclose = (event) => {
      console.log('WebSocketService: Closed, code=' + event.code + ', reason=' + event.reason);
      this.cleanup();
      if (!this.manualClose) {
        this.callbacks.onStatusChange('reconnecting');
        this.scheduleReconnect();
      } else {
        this.callbacks.onStatusChange('disconnected');
      }
    };

    this.ws.onerror = (event) => {
      console.error('WebSocketService: Error:', event);
    };

    this.ws.onmessage = (event) => {
      // Plain string pong — ignore (keepalive response)
      if (event.data === 'pong') {
        return;
      }
      try {
        const msg: WSServerMessage = JSON.parse(event.data);
        console.log('WS RECV:', event.data);
        this.callbacks.onMessage(msg);
      } catch (e) {
        console.error('WebSocketService: Failed to parse message:', event.data, e);
      }
    };
  }

  disconnect(): void {
    this.manualClose = true;
    this.clearReconnectTimer();
    this.cleanup();

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, 'client disconnect');
      }
      this.ws = null;
    }

    this.callbacks.onStatusChange('disconnected');
  }

  send(msg: object): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocketService: Cannot send, not connected, readyState=' + this.ws?.readyState);
      return;
    }
    const json = JSON.stringify(msg);
    console.log('WS SEND:', json);
    this.ws.send(json);
  }

  subscribe(target: SubscriptionTarget): void {
    this.send({ type: 'subscribe', target });
  }

  unsubscribe(target?: SubscriptionTarget): void {
    if (target) {
      this.send({ type: 'unsubscribe', target });
    } else {
      this.send({ type: 'unsubscribe' });
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, PING_INTERVAL);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private cleanup(): void {
    this.stopPing();
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();

    console.log('WebSocketService: Reconnecting in ' + this.reconnectDelay + 'ms');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_DELAY);
      this.connect();
    }, this.reconnectDelay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
